import React, {useCallback, useState} from 'react'
import {Button, message, Modal} from "antd";
import utils from "../../utils";
import FormService from "../../services/api/form";
import moment from "moment";
import {PUBLIC_PREFIX_PATH} from "../../configs/AppConfig";

const { info } = Modal;

export class CustomButton {
  constructor(obj = {}) {
    Object.assign(this, {
      name: obj.name,
      type: obj.type ?? 'action',
      size: obj.size,
      color: obj.color,
      url: obj.url,
      operation: obj.operation,
      data: obj.data
    });
  }

  getComponent(form, history) {
    const [isSaving, setIsSaving] = useState(false);


    const saveHandler = useCallback((withParams = false, withReload = false) => {
      if(form.isFieldsTouched()) {
        setIsSaving(true);
        this.validateForm(form)
          .then(values => {
            const saveOptions = {
              id_form: utils.getGenericIdLocation(),
              id_patient: utils.getPatientLocation(),
              form: values,
              ...(withParams && {params: utils.getJsonSearch()}),
              ...(withReload && {id_secondary: utils.getSecondGenericIdLocation()})
            };

            const saveMethod = withParams ? FormService.saveWithParams : FormService.save;

            saveMethod(saveOptions)
              .then(res => {
                message.success("Guardado realizado");
                resetTouched();
                if (withReload) {
                  const refresh = Math.floor(Math.random() * 1000);
                  history.push(`${this.getRootPrefix()}/dashboard/${utils.getIdDashboard()}${utils.getSecondGenericIdLocation() ? '/' + utils.getSecondGenericIdLocation() : ""}?refresh=${refresh}${utils.getPatientLocation() ? '&idPatient=' + utils.getPatientLocation() : ''}`);
                } else if (res?.url) {
                  // Comprobar si es una URL externa (comienza con http)
                  if (res.url.startsWith('http')) {
                    // Redirección externa usando window.location
                    window.location.href = res.url;
                  } else {
                    // Redirección interna usando history
                    history.push(utils.getRedirectWithPatient(res.url));
                  }
                }
              })
              .catch(() => message.error("Error al guardar el formulario."))
              .finally(() => {
                setIsSaving(false);
              });
          })
          .catch(() => setIsSaving(false));
      } else {
        message.warning("No se ha modificado ningún campo.");
      }
    }, [form, history]);

    // Valida siempre y guarda solo si hay cambios; si algo falla, no se imprime
    const saveBeforePrint = useCallback(() => {
      return this.validateForm(form)
        .then(values => {
          if (!form.isFieldsTouched()) return;
          setIsSaving(true);
          return FormService.save({
            id_form: utils.getGenericIdLocation(),
            id_patient: utils.getPatientLocation(),
            form: values
          })
            .then(() => {
              message.success("Guardado realizado");
              resetTouched();
            })
            .finally(() => setIsSaving(false));
        });
    }, [form]);

    const onClickButton = useCallback(() => {
      const withSave = (fn) => () => saveBeforePrint().then(fn).catch(() => {});


      const handlers = {
        'save': () => saveHandler(false, false),
        'saveWithParams': () => saveHandler(true, false),
        'saveWithParamsReload': () => saveHandler(true, true),
        'clear': () => form.resetFields(),
        'download': () => window.open(this.url),
        'back': () => history.goBack(),
        'backTwice': () => {
          history.goBack();
          setTimeout(() => history.goBack(), 100);
        },
        'redirect': () => window.location.replace(this.url),
        'externalRedirect': () => window.open(this.url, '_blank'),
        'link': () => history.push(this.url),
        'printer': withSave(() => window.print()),
        'print': withSave(() => FormService.callApi({operation: this.operation, id_form: utils.getGenericIdLocation(), id_patient: utils.getPatientLocation(), form: form.getFieldsValue()})
          .then(res => window.open(res?.data?.url, '_blank'))),
        'printText': withSave(() => {
          FormService.callApi({
            operation: this.operation,
            id_form: utils.getGenericIdLocation(),
            id_patient: utils.getPatientLocation(),
            form: form.getFieldsValue()
          })
            .then(res =>
              info({
                title: "Resultados en texto",
                closable: true,
                width: "80%",
                content: <pre style={{
                  maxHeight: "35rem",
                  overflow: "auto",
                  backgroundColor: "#fafafb",
                  padding: "10px"
                }}>{res.data.texto}</pre>,
                okText: "Copiar",
                onOk() {
                  navigator.clipboard.writeText(res.data.texto)
                    .then(() => {
                      message.success('Texto copiado al portapapeles', 3);
                    })
                    .catch(err => {
                      console.error('Error al copiar el texto: ', err);
                    });
                },
                onCancel() {
                  console.log('Cancel');
                },
              })
            )
        }),
        'operation': () => { FormService.callApi({operation: this.operation, id_form: utils.getGenericIdLocation(), id_patient: utils.getPatientLocation(), form: form.getFieldsValue()})},
        'printDashboard': () => {
          FormService.callApi({
            operation: this.operation,
            id_form: utils.getGenericIdLocation(),
            id_patient: utils.getPatientLocation(),
            data: this.data
          })
            .then(res =>
              info({
                title: "Resultados en texto",
                closable: true,
                width: "80%",
                content: <pre style={{
                  maxHeight: "35rem",
                  overflow: "auto",
                  backgroundColor: "#fafafb",
                  padding: "10px"
                }}>{res.data.texto}</pre>,
                okText: "Copiar",
                onOk() {
                  navigator.clipboard.writeText(res.data.texto)
                    .then(() => message.success('Texto copiado al portapapeles', 3))
                    .catch(err => console.error('Error al copiar el texto: ', err));
                },
              })
            )
        },
        'printerApi': withSave(() => {
          FormService.callApi({
            operation: this.operation,
            project: utils.getProjectLocation(),
            hopital: utils.getHospitalLocation(),
            id_form: utils.getGenericIdLocation(),
            id_patient: utils.getPatientLocation(),
            form: form.getFieldsValue()
          })
          window.print()
        }),
        'no-token-filter': () => {
          history.push(`/public/${utils.getProjectLocation()}/fixed/dashboard/${utils.getIdDashboard()}${this.getURLWithFilter(form.getFieldsValue())}`);
        }
      };

      (handlers[this.type] || this.defaultHandler)(form, history);
    }, [this.type, this.url, form, history, saveHandler, saveBeforePrint]);



    const resetTouched = () => { form.setFields( Object.keys(form.getFieldsValue(true)).map((name) => ({ name, touched: false, })) ); console.log("Touched reset"); };

    return (
      <Button
        className="mr-2"
        type={this.color}
        loading={isSaving}
        htmlType="submit"
        onClick={onClickButton}
      >
        {this.name}
      </Button>
    );
  }
  validateForm(form) {
    return new Promise((resolve, reject) => {
      form.validateFields()
        .then(resolve)
        .catch(errorInfo => {
          const firstErrorField = errorInfo.errorFields[0]?.name;
          if (firstErrorField) {
            form.scrollToField(firstErrorField, {
              behavior: 'smooth',
              block: 'center',
            });
          }
          message.error("Por favor corrige los errores en el formulario.");
          reject(errorInfo);
        });
    });
  }

  getRootPrefix = () => {
    const isPublic = window.location.pathname.startsWith(PUBLIC_PREFIX_PATH);
    return isPublic
      ? `${PUBLIC_PREFIX_PATH}/${utils.getProjectLocation()}/fixed`
      : utils.getInitRootProject();
  }

  defaultHandler = (form, history) => {
    history.push(`${this.getRootPrefix()}/dashboard/${utils.getIdDashboard()}${this.getURLWithFilter(form.getFieldsValue())}`);
  }

  getURLWithFilter = (data) => {
    const searchUrl = utils.getJsonSearch();
    let uri = searchUrl.idPatient ? '?idPatient='+searchUrl.idPatient+"&" : '?';
    Object.keys(data).forEach(o => {
      if(data[o] !== undefined && data[o] !== '' && data[o] !== null) {
        if(data[o][0]?.key || data[o].constructor.name == 'Object')
        {
          let test = JSON.stringify(data[o]);
          let buff = new Buffer(test);
          data[o] = buff.toString('base64');
          data[o] = 'base64-'+data[o];
        }
        if(moment.isMoment(data[o]))
          data[o] = data[o].format('DD-MM-YYYY');
        else if(typeof data[o] === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(data[o]))
          data[o] = moment(data[o], 'YYYY-MM-DD').format('DD-MM-YYYY');
        else if(typeof data[o] === 'string' && /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}$/.test(data[o]))
          data[o] = moment(data[o], 'YYYY-MM-DD HH:mm').format('DD-MM-YYYY HH:mm');
        else if(typeof data[o] === 'string' && /^\d{4}-\d{2}-\d{2},\d{4}-\d{2}-\d{2}$/.test(data[o]))
          data[o] = data[o].split(',').map(d => moment(d, 'YYYY-MM-DD').format('DD-MM-YYYY')).join(',');
        else if(typeof data[o] === 'string' && /^\d{2}\/\d{2}\/\d{4},\d{2}\/\d{2}\/\d{4}$/.test(data[o]))
          data[o] = data[o].split(',').map(d => moment(d, 'DD/MM/YYYY').format('DD-MM-YYYY')).join(',');
        if(moment.isMoment(data[o][0])){
          let start = data[o][0].format('DD-MM-YYYY');
          let end = data[o][1].format('DD-MM-YYYY');
          data[o] = start+','+end
        }
        //Comprueba si tiene saltos de línea y los mete en la url
        uri += `${o}=${encodeURIComponent(data[o])}&`;
      }
    })
    return uri;
  }

  transformToField = (data) => {
    this.name = data.name;
    this.label = data.label;
    this.type = data.type;
    this.color = data.color;
    this.size = data.size ?? this.size;
    this.url = data.url;
    this.operation = data.operation;
    this.data = data.data;
    return this;
  }
}